import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
const Layout = lazy(() => import("@/components/Layout"));
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SimulationProvider } from "@/components/SimulationProvider";

// Lazy-loaded pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const Documentations = lazy(() => import("@/pages/Documentations"));
const ProjectTracking = lazy(() => import("@/pages/ProjectTracking"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const ClientsManager = lazy(() => import("@/pages/admin/ClientsManager"));
const TasksManager = lazy(() => import("@/pages/admin/TasksManager"));
const InterventionsManager = lazy(() => import("@/pages/admin/Interventions"));
const TicketsManager = lazy(() => import("@/pages/admin/Tickets"));
const AccessManager = lazy(() => import("@/pages/admin/AccessManager"));
const ProjectsManager = lazy(() => import("@/pages/admin/ProjectsManager"));
const ProjectMonitoringHub = lazy(() => import("@/pages/admin/ProjectMonitoringHub"));
const AuditLogs = lazy(() => import("@/pages/admin/AuditLogs"));
const StatsDashboard = lazy(() => import("@/pages/admin/StatsDashboard"));
const ReportsDashboard = lazy(() => import("@/pages/admin/ReportsDashboard"));
const GodModeManager = lazy(() => import("@/pages/admin/GodModeManager"));
const Profile = lazy(() => import("@/pages/Profile"));
const DocumentsManager = lazy(() => import("@/pages/admin/DocumentsManager"));
const Opportunities = lazy(() => import("@/pages/admin/Opportunities"));
const PendingValidation = lazy(() => import("@/pages/PendingValidation"));

const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-domotus-bg text-domotus-text space-y-4">
    <div className="h-12 w-12 border-4 border-domotus-accent/20 border-t-domotus-accent rounded-full animate-spin"></div>
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-secondary animate-pulse">Chargement de votre espace...</p>
  </div>
);


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-domotus-bg text-domotus-text">Chargement...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.status === "en_attente") {
    // We allow the /pending route itself
    if (window.location.pathname === "/pending") {
      return <>{children}</>;
    }
    return <Navigate to="/pending" replace />;
  }

  // If user is active but on /pending, redirect to home
  if (user.status === "actif" && window.location.pathname === "/pending") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="domotus-theme">
      <AuthProvider>
        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/pending" element={<ProtectedRoute><PendingValidation /></ProtectedRoute>} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <SimulationProvider>
                      <Layout>
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                            <Route path="/suivi-chantier" element={<ProjectTracking />} />
                            <Route path="/documentations" element={<Documentations />} />
                            <Route path="/documents-techniques" element={<Navigate to="/documentations" replace />} />
                            <Route path="/documents-administratifs" element={<Navigate to="/documentations" replace />} />
                            <Route path="/contrat-maintenance" element={<Maintenance />} />
                            <Route path="/profile" element={<Profile />} />

                            {/* Admin Routes */}
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/admin/projects" element={<ProjectsManager />} />
                            <Route path="/admin/clients" element={<ClientsManager />} />
                            <Route
                              path="/admin/interventions"
                              element={<ProjectMonitoringHub />}
                            />
                            <Route path="/admin/tasks" element={<ProjectMonitoringHub />} />
                            <Route path="/admin/tickets" element={<TicketsManager />} />
                            <Route path="/admin/access" element={<AccessManager />} />
                            <Route path="/admin/stats" element={<StatsDashboard />} />
                            <Route path="/admin/reports" element={<ReportsDashboard />} />
                            <Route path="/admin/documents" element={<DocumentsManager />} />
                            <Route path="/admin/god-mode" element={<GodModeManager />} />
                            <Route path="/admin/logs" element={<AuditLogs />} />
                            <Route path="/admin/opportunities" element={<Opportunities />} />
                            <Route path="/admin/quoting" element={<div className="p-8 text-secondary italic font-black uppercase tracking-widest text-xs">Module Devis & Chiffrage en cours de développement.</div>} />
                            <Route path="/admin/offers" element={<div className="p-8 text-secondary italic font-black uppercase tracking-widest text-xs">Module Offres & Services en cours de développement.</div>} />
                          </Routes>
                        </Suspense>
                      </Layout>
                    </SimulationProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}
